import { unzipSync } from 'fflate';
import jobListingsService from './JobListingsService';
import assessmentService from './AssessmentService';
import jobDocumentService from './JobDocumentService';
import { groupZipEntries, entryToFile } from '../utils/jobZip';
import { parseInvitationEmail } from '../utils/invitationEmail';

/**
 * One upload, two systems.
 *
 * The ZIP is unpacked in the browser rather than sent whole to the CMS, because
 * its parts belong to different places: the campaign page and its JSON to the
 * CMS; the questionnaire, its invitation mail, the Indeed and AfA texts and a
 * copy of the JSON and storyboard to PocketBase. The CMS cannot write to
 * PocketBase, and giving it the ability would be worse than this.
 *
 * Order matters. The CMS import comes first and returns the job's id, which is
 * what the questionnaire is keyed by. Without that id there is nothing to
 * attach it to.
 */
export async function importZip(file, onProgress) {
  let entries;
  try {
    entries = unzipSync(new Uint8Array(await file.arrayBuffer()));
  } catch {
    return { success: false, error: 'Die Datei ist kein lesbares ZIP-Archiv.' };
  }

  const { jobs, skipped } = groupZipEntries(entries);
  if (jobs.length === 0) {
    return {
      success: false,
      error: skipped.length
        ? 'Keine vollständige Stelle im ZIP gefunden. Jede braucht HTML und GoogleJobs-JSON.'
        : 'Das ZIP enthält keine Stellen.',
    };
  }

  const results = skipped.map((entry) => ({
    baseName: entry.name,
    success: true,
    action: 'skipped',
    reason: entry.reason,
  }));

  for (let index = 0; index < jobs.length; index += 1) {
    const job = jobs[index];
    onProgress?.({ done: index, total: jobs.length, current: job.baseName });

    const cmsResult = await jobListingsService.importPair(
      entryToFile(job.campaign, 'text/html'),
      entryToFile(job.json, 'application/json')
    );

    if (!cmsResult.success) {
      results.push({
        baseName: job.baseName,
        success: false,
        action: 'failed',
        error: cmsResult.error,
      });
      continue;
    }

    // The CMS answers with one row per pair; a single pair means one row.
    const row = cmsResult.data?.results?.[0];
    if (!row) {
      results.push({
        baseName: job.baseName,
        success: false,
        action: 'failed',
        error: 'Das CMS hat keine Rückmeldung zu dieser Stelle geliefert.',
      });
      continue;
    }

    results.push({ ...row, baseName: job.baseName });

    // Only worth attempting once the CMS knows the job. A skipped or failed row
    // carries no id, and nothing in PocketBase can hang on a job without one.
    if (!row.jobId) continue;

    if (job.assessment) {
      const invitation = job.invitation ? parseMail(job.invitation) : null;
      const saved = await assessmentService.save({
        jobId: row.jobId,
        label: row.jobTitle || '',
        htmlFile: entryToFile(job.assessment, 'text/html'),
        emailSubject: invitation?.subject || '',
        emailBody: invitation?.body || '',
        existingId: (await assessmentService.getByJobId(row.jobId)).data?.id,
      });

      results.push({
        baseName: `${job.baseName} · Fragebogen 2`,
        success: saved.success,
        action: saved.success ? 'created' : 'failed',
        error: saved.success ? undefined : saved.error,
        reason: saved.success && !invitation ? 'Ohne Einladungs-Mail hochgeladen.' : undefined,
      });
    }

    results.push(await attachDocuments(job, row));
  }

  onProgress?.({ done: jobs.length, total: jobs.length, current: null });

  const summary = {
    total: results.length,
    created: results.filter((r) => r.action === 'created').length,
    updated: results.filter((r) => r.action === 'updated').length,
    skipped: results.filter((r) => r.action === 'skipped').length,
    failed: results.filter((r) => r.action === 'failed').length,
  };

  return { success: true, data: { results, summary } };
}

/**
 * Store what PocketBase keeps of a job besides the questionnaire: the Indeed
 * and AfA texts, and the originals the download hands back.
 *
 * Only the files present in the ZIP are written, so an archive without an AfA
 * file leaves an existing AfA text alone. The JSON always is, since a job
 * without one never gets this far.
 */
async function attachDocuments(job, row) {
  const parts = [
    'JSON',
    job.storyboard && 'Storyboard',
    job.invitation && 'Mail',
    job.indeed && 'Indeed',
    job.afa && 'AfA',
  ].filter(Boolean);
  const baseName = `${job.baseName} · ${parts.join(', ')}`;

  const existing = await jobDocumentService.getByJobId(row.jobId);
  if (!existing.success) {
    return { baseName, success: false, action: 'failed', error: existing.error };
  }

  const saved = await jobDocumentService.save({
    jobId: row.jobId,
    label: row.jobTitle || '',
    baseName: job.baseName,
    // Originals verbatim: nothing edits them, and the download should give
    // back exactly the bytes that came in.
    googleJobsJson: readText(job.json),
    storyboardMd: job.storyboard ? readText(job.storyboard) : undefined,
    emailMarkdown: job.invitation ? readText(job.invitation) : undefined,
    indeedText: job.indeed ? decodeText(job.indeed) : undefined,
    afaText: job.afa ? decodeText(job.afa) : undefined,
    existingId: existing.data?.id,
  });

  return {
    baseName,
    success: saved.success,
    action: !saved.success ? 'failed' : existing.data ? 'updated' : 'created',
    error: saved.success ? undefined : saved.error,
  };
}

function readText(entry) {
  return new TextDecoder().decode(entry.bytes);
}

/**
 * The ad texts are edited in a text field, which only knows one kind of line
 * break, so they are stored with that one from the start.
 */
function decodeText(entry) {
  return readText(entry).replace(/\r\n?/g, '\n').trimEnd();
}

/** Decode and parse an invitation mail, or null when it does not fit the shape. */
function parseMail(entry) {
  const text = new TextDecoder().decode(entry.bytes);
  const parsed = parseInvitationEmail(text);
  return parsed.ok ? parsed : null;
}
